import { ComponentType, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useBoatActions, useBoatLength, useSelectedBoat } from '@/entities/boat';
import { useCommonModal } from '@/entities/modal';
import { SearchParams } from '@/shared/constants/search-params.constant';
import { useNamespace } from '@/shared/lib/hooks/use-namespace.hook';
import { useRouteParams } from '@/shared/lib/hooks/use-route-params.hook';
import { Box } from '@/shared/ui/box';
import { Text } from '@/shared/ui/text';

import { SelectBoat } from '../../../ui/select-boat';
import * as Styled from './with-selected-boat.styles';

interface ComponentWithSelectedBoatProps {
	id: string;
	uuid: string;
}

/**
 * HOC for components to be used with the choice of boat
 * @param {ComponentType<T & ComponentWithSelectedBoatProps>} [Component] - Wrapped component
 * @returns FC<T>
 * @example
 * const PageWithSelectedBoat = withSelectedBoat(({ uuid, id }) => {
 * 	const {
 * 		data
 * 	} = useGetSomethingWithUuidAndId({ uuid, id });
 *
 * 	return (
 * 		<Box>
 * 		 ...
 * 		</Box>
 * 	);
 * });
 */

export const withSelectedBoat =
	<T extends {}>(Component: ComponentType<ComponentWithSelectedBoatProps>): FC<T> =>
	(props: T) => {
		const { id } = useRouteParams();
		const namespace = useNamespace('common');
		const [searchParams] = useSearchParams();
		const { setBoatById } = useBoatActions();
		const selectedBoat = useSelectedBoat();
		const boatsLength = useBoatLength();
		const renderPopup = useRef(true);
		const [checked, setChecked] = useState(false);
		const [openModal, hideModal] = useCommonModal();
		const { t } = useTranslation(namespace as ['plant', 'common'] | 'common');
		const boatId = searchParams.get(SearchParams.BoatId);

		useEffect(() => {
			if (boatId || id) {
				setBoatById(boatId || id);
			}
			setChecked(true);

			return () => {
				renderPopup.current = true;
			};
		}, [id, boatId, setBoatById]);

		useEffect(() => {
			if (checked && !selectedBoat && boatsLength > 0 && renderPopup.current) {
				openModal({
					title: t('modal.single.title'),
					description: t('modal.single.description'),
					overflowVisible: true,
					content: (
						<Styled.SelectWrapper>
							<SelectBoat redirect={false} withoutAll />
						</Styled.SelectWrapper>
					),
				});
				renderPopup.current = false;
			}

			return () => {
				hideModal();
			};
		}, [boatsLength, checked, hideModal, openModal, selectedBoat, t]);

		if (!checked) {
			return null;
		}

		if (!selectedBoat) {
			return (
				<Box align="center" justify="center" height="100%" padding="12px">
					<Text
						align="center"
						weight={500}
						label={boatsLength ? t('modal.single.description') : t('modal.boat.empty')}
					/>
				</Box>
			);
		}

		return <Component {...props} uuid={selectedBoat.uuid} id={selectedBoat.id} />;
	};
